import { Box, BoxProps } from '@mui/material'
import { ReactNode } from 'react'
import radii from 'src/theme/radii'
import {
  codeTag as codeTagTokens,
  colorTokens,
  spaceUnit,
  typography as typeTokens,
} from 'src/theme/tokens.generated'

// ----------------------------------------------------------------------
// CodeTag: a monospace "code" badge for identifiers: licence plates, VINs,
// booking codes (the design's `.code-tag`). Keeps codes visually distinct from
// prose and consistent everywhere. Colour/spacing come from the theme.
//
// It does not wrap, and that is right for everything it was built for: a plate
// or a VIN broken across two lines is harder to read, not easier, and there is
// always room for one. An opaque 28-character id is the case it is wrong for:
// there is no room, there is no break opportunity, and the card's own
// `overflow: hidden` silently took the rest of the string. `wrap` is opt-in so
// every existing site keeps the one-line box it has today.
//
// **The edge is `borderStrong`, and it is a token rather than the default
// hairline.** The tag's own fill is the sunken ground and it sits inside a
// card that is `surface`, so the divider role (1.10:1 against paper) drew no
// boundary at all: the Developer panel's SQL ID read as a smudge of fill
// rather than as a tag, which is the report this answers. `borderStrong` is
// already the role this console reaches for when a rule has to be seen against
// a card: `panelEdge` in `overrides/Table.ts` is the same argument for a
// panel frame. `controlEdge` is rejected: that is the role for a control with
// nothing but its boundary to say it exists, and twenty identifier chips
// outlined in mid-grey stop being tags and become boxes.
//
// **The face is the token's, and the console ships it.** The family was
// written here as `ui-monospace, "SF Mono", Menlo, monospace`, which names
// nothing a Windows machine has: Chrome does not support `ui-monospace`, and
// the other two are Apple's. So on a Windows laptop every plate fell through
// to the generic monospace, Consolas or Courier New, with smaller capitals,
// no 500 to draw the weight below with, and in Courier a zero that is a
// capital O. The stack is `typography.families.mono` now, and its first
// choice, IBM Plex Mono, is served from `public/fonts`.
//
// **The box is a whole number of pixels, from `codeTag.height`.** It was
// whatever its padding and a 1.45 line came to: 23.4px, which is 29.25 device
// pixels at Windows' 125 percent and 35.1 at 150, so wherever its top edge
// fell, its bottom edge fell between two rows of device pixels. The line is
// stated in pixels so that the edge, the inset and the line add up to the
// token exactly, and a tag that wraps grows by whole lines.
//
// It is read as `colorTokens[mode].borderStrong` and not written as
// `borderColor: 'borderStrong'`, because that name is not on the MUI palette:
// the string would pass through untranslated, land in CSS as an invalid
// colour, and the chip would silently keep the boundary it has now.
// ----------------------------------------------------------------------

/** The tag's edge, all four sides, in pixels. */
const EDGE = 1

/** The inset above and below the words, in spacing steps (2px). */
const INSET_BLOCK = 0.25

/**
 * One line of the tag, in pixels: whatever of the token's height the edge and
 * the inset leave. 18 at 12px type, a 1.5 line where it was 1.45 (17.4px), and
 * a whole number, which is what makes the box one.
 */
export const CODE_TAG_LINE =
  codeTagTokens.height - 2 * (EDGE + INSET_BLOCK * spaceUnit)

type CodeTagProps = BoxProps & {
  children: ReactNode
  /** Let a long, unbreakable id break rather than run under the card's edge. */
  wrap?: boolean
}

export default function CodeTag({
  children,
  wrap = false,
  sx,
  ...other
}: CodeTagProps) {
  return (
    <Box
      component="span"
      // A stable hook, so "this identifier is drawn as one tag, not a tag
      // inside a tag" and "an absent identifier leaves no empty tag" are
      // things a test can state rather than infer from text content.
      data-testid="code-tag"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        width: 'fit-content',
        // The edge inside the token's height, stated here rather than left to
        // the baseline, so the 24 does not depend on what the page around the
        // tag resets.
        boxSizing: 'border-box',
        // `height` on one line, so nothing a site puts inside can stretch it
        // off the token; `minHeight` when it may wrap, so it grows by lines.
        height: wrap ? 'auto' : codeTagTokens.height,
        minHeight: codeTagTokens.height,
        fontFamily: typeTokens.families.mono.stack,
        fontSize: 12,
        // The token, not MUI's alias for it. `fontWeightMedium` resolves
        // through `theme.typography`, and this theme binds that name to
        // `weights.semibold`: 600, which is the exact weight `KeyValueRow`
        // gives a *reading*. So an identifier meant to be quieter than the
        // value beside it was set at the same weight as one, and the name
        // read as though it were asking for 500 while asking for 600.
        fontWeight: typeTokens.weights.medium,
        letterSpacing: '0.02em',
        color: 'text.secondary',
        bgcolor: 'background.neutral',
        border: `${EDGE}px solid`,
        borderColor: (theme: import('@mui/material/styles').Theme) =>
          colorTokens[theme.palette.mode][codeTagTokens.edge],
        borderRadius: radii.inline,
        px: 0.75,
        py: INSET_BLOCK,
        lineHeight: `${CODE_TAG_LINE}px`,
        // `anywhere` and not `break-word`: it is the one that also lets the
        // flex or grid box shrink below the token's own width, which is what
        // the value column of a key-value row needs before it can give the tag
        // a line to break on.
        whiteSpace: wrap ? 'normal' : 'nowrap',
        ...(wrap ? { overflowWrap: 'anywhere', minWidth: 0 } : null),
        ...sx,
      }}
      {...other}
    >
      {children}
    </Box>
  )
}
